'use client';

/**
 * What is left, where the order is confirmed.
 *
 * The pool was only visible as a ring in the header, which a parent on the
 * review screen is not looking at: they learned the allowance was short at the
 * moment the order refused to start (gap 4). The line says how much is left in
 * the units the product sells — stories and narrations, never raw credits
 * (`SHARED.MONEY.R1`) — and when nothing is left it says so before the press
 * rather than after it.
 */

import { useLocale, useTranslations } from 'next-intl';
import { useAllowanceMeter } from '../hooks/useAllowanceMeter';
import { openSubscription } from '../lib/accountsTopUp';

export function AllowanceLeftNote({ wantsBook }: { wantsBook?: boolean }) {
  const t = useTranslations('subscription.allowance');
  const locale = useLocale();
  const { data } = useAllowanceMeter();

  if (!data) return null;

  if (!data.hasAllowance) {
    return (
      <p className="text-xs text-warning-foreground dark:text-warning" data-testid="allowance-left-empty">
        {t('leftNothing')}{' '}
        <button
          type="button"
          onClick={() => openSubscription(locale)}
          className="font-medium text-primary hover:underline"
          data-testid="allowance-left-subscribe"
        >
          {t('subscribe')}
        </button>
      </p>
    );
  }

  const parts: string[] = [];
  // The artefact this order makes comes first — that is the number the parent
  // is deciding with.
  if (wantsBook && data.estimates.smallBook != null) {
    parts.push(t('smallBooks', { count: data.estimates.smallBook }));
  }
  if (data.estimates.voiceover != null) parts.push(t('voiceovers', { count: data.estimates.voiceover }));
  if (!wantsBook && data.estimates.smallBook != null) {
    parts.push(t('smallBooks', { count: data.estimates.smallBook }));
  }
  if (parts.length === 0) return null;

  return (
    <p className="text-xs text-muted-foreground" data-testid="allowance-left">
      {t('leftLine', { parts: parts.join(' · ') })}
    </p>
  );
}
